import React from "react";
import {getDiffDate} from "./dates";
import {OVERDUE_COLOR_MAP} from "../constants/Colors";
import {normalizeColor} from "grommet/utils";
import Badge from "../components/Badge";
import { CHECKOUT_AMOUNT } from "../constants/Checkout";
import { MILESTONE_AMOUNT_TYPE } from "../constants/Invoice";
import { formatCurrency } from "./numbers";

export const getInvoiceShortLink = (data) => {
    const shortLinkDomain = process.env.NODE_ENV === 'production'
        ? (process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ? "dev.s9d.cc" : "s9d.cc")
        : "dev.s9d.cc";
    return `https://${shortLinkDomain}/${data?.slug}`;
};

export const getInvoiceBalance = (invoiceData) => {
    if (!invoiceData?.balance || !invoiceData?.pending) {
        return 'error';
    }
    const {
        balance: {
            amount: balanceAmount,
        },
        pending: {
            amount: pendingAmount
        }
    } = invoiceData || {};
    return balanceAmount - pendingAmount;
}

export const getSortedTransactions = (invoiceData) => {
    const transactions = invoiceData?.transactions || [];
    const pendingTransactions = invoiceData?.pending_transactions || [];
    return [...transactions, ...pendingTransactions].sort(({created_at: a}, {created_at: b}) => {
        const date1 = new Date(a);
        const date2 = new Date(b);
        if (date1 > date2) return 1;
        if (date1 < date2) return -1;
        return 0;
    });
}

export const getOverdueRange = (dueDate) => {
    const diffDate = getDiffDate(dueDate, new Date(), false, true);
    if (diffDate > 90) return {range: "90+", label: "Overdue 91+"};
    if (diffDate > 60) return {range: "60+", label: "Overdue 60+"};
    if (diffDate > 30) return {range: "30+", label: "Overdue 30+"};
    if (diffDate > 0) return {range: "0+", label: "Overdue"}
    return null;
}

export const getInvoiceStatusBadge = ({theme, ...data}) => {
    let color = "invoice-not-yet-due"; // default
    let label = "Not yet due";

    if (data.is_voided || data.voided) {
        color = "invoice-voided";
        label = "VOIDED";
    } else if (data.is_pending_close || data?.invoice?.is_pending_close === "Processing") {
        color = "invoice-processing";
        label = "PROCESSING";
    }
    else if (data.invoice_status === "Has Credit") {
        color = "invoice-has-credit";
        label = "CREDIT";
    } else if (data.invoice_status === "Closed") {
        color = "invoice-closed";
        label = data.invoice_status;
    } else if (data.overdue_range && data?.invoice_status === "Overdue") {
        color = OVERDUE_COLOR_MAP[data.overdue_range.range];
        label = data.overdue_range.label;
    }
    return (
        <Badge
            background={{
                color: `${normalizeColor(theme.global.colors[color], theme)}`
            }}>
            { label }
        </Badge>
    );
}

export const getMilestoneStatusBadge = ({theme, milestone}) => {
    let color = "invoice-not-yet-due"; // default
    let label = "Not yet due";
    const overdueRange = milestone?.due_date ? getOverdueRange(new Date(milestone?.due_date)) : null;

    if (!milestone?.has_been_issued) {
        color = 'status-disabled';
        label = "Not issued";
        return (<></>)
    } else if (milestone?.is_voided) {
        color = 'invoice-voided';
        label = "Voided";
    } else if (milestone?.current_open_balance <= 0) {
        color = 'invoice-closed';
        label = 'Paid';
    } else if (overdueRange) {
        color = OVERDUE_COLOR_MAP[overdueRange.range];
        label = overdueRange.label;
    }
    
    return !label  ? (<></>) : (
        <Badge
            background={{
                color: `${normalizeColor(theme.global.colors[color], theme)}`
            }}>
            { label }
        </Badge>
    );
}

export const renderPOnumber = (poNumber) => {
    if(!poNumber){return ''}
    return poNumber.trim().replace(/^(PO)?(\s+)?(#|-)?(\s+)?/g, '');
}

export const countNumberOfIssuedMilestones = (milestones) => {
    if (!Array.isArray(milestones)) return 0;
    let count = 0;
    for (let i = 0; i < milestones.length; i++) {
        const milestone = milestones[i];
        if (milestone?.has_been_issued) count++;
    }
    return count;
}

export const getInvoiceAmount = ({balanceNetPendingPayment, balanceType}) => { 
    if (!balanceNetPendingPayment) return undefined;
    const {
        total_overdue_milestone_amount: totalOverdue,
        total_due_milestone_amount: totalDue,
        amount: totalOpenBalance
    } = balanceNetPendingPayment;

    switch (balanceType) {
    case (CHECKOUT_AMOUNT.TOTAL_PAST_DUE): return totalOverdue
    case (CHECKOUT_AMOUNT.TOTAL_DUE): return totalDue;
    case (CHECKOUT_AMOUNT.TOTAL_OPEN_BALANCE): return totalOpenBalance;
    }
    return undefined; // shouldn't fall to there
}

export const isMilestoneInvoice = (data) => {
    return data?.invoice?.billing_milestones?.length > 0;
}

export const isMilestoneInvoiceAndHasNoIssuedMilestone = (data) => {
    if (!data || !isMilestoneInvoice(data)) return false;
    const milestones = data.invoice.billing_milestones;
    for (let i = 0; i < milestones.length; i++) {
        const milestone = milestones[i];
        if (milestone.has_been_issued) return false;
    }
    return true;
}

/**
 * Calculate the total of all fixed dollar milestone amounts
 */
export const getMilestoneDollarTotal = (milestones) => {
    return milestones?.reduce((total, m) => {
        return m.amount_type !== MILESTONE_AMOUNT_TYPE.PERCENTAGE
            ? total + (Number(m.amount) || 0)
            : total;
    }, 0) || 0;
};

/**
 * Calculate the remaining amount available for percentage-based milestones
 */
export const getMilestoneRemainingForPercentage = (milestones, invoiceTotal) => {
    const dollarTotal = getMilestoneDollarTotal(milestones);
    return Math.max(0, invoiceTotal - dollarTotal);
};

/**
 * Format a milestone amount for display
 * - Dollar milestones: "$1,000.00"
 * - Percentage milestones: "$250.00 (25%)" — percentage is of invoice total
 */
export const formatMilestoneAmount = (milestone, milestones, invoiceTotal, currency = 'USD') => {
    if (milestone.amount_type === MILESTONE_AMOUNT_TYPE.PERCENTAGE) {
        const resolvedAmount = (Number(milestone.amount) / 100) * invoiceTotal;
        return `${formatCurrency(resolvedAmount, currency)} (${milestone.amount}%)`;
    }
    return formatCurrency(milestone.amount, currency);
};